import axios from 'axios';
import { API_BASE_URL, USE_MOCK_API, MOCK_DELAY } from '../constants';
import indexedDBService from './indexedDBService';
import type {
  User,
  Model,
  Chat,
  Config,
  Folder,
  Tag,
  Tool,
  Banner,
  Knowledge,
} from '../types';

// Platform detection
export const detectPlatform = (): 'web' | 'electron' | 'vscode' => {
  if (typeof window !== 'undefined' && (window as any).electronAPI) {
    return 'electron';
  }
  if (typeof window !== 'undefined' && (window as any).acquireVsCodeApi) {
    return 'vscode';
  }
  return 'web';
};

// Platform-specific file operations
export const platformFileOps = {
  readFile: async (filePath: string): Promise<string> => {
    const platform = detectPlatform();
    switch (platform) {
      case 'electron':
        return (window as any).electronAPI.readFile(filePath);
      case 'vscode':
        // Send message to VSCode extension
        const vscode = (window as any).acquireVsCodeApi();
        return new Promise((resolve, reject) => {
          const handler = (event: MessageEvent) => {
            if (event.data.type === 'fileRead' && event.data.filePath === filePath) {
              window.removeEventListener('message', handler);
              if (event.data.success) {
                resolve(event.data.content);
              } else {
                reject(new Error(event.data.error));
              }
            }
          };
          window.addEventListener('message', handler);
          vscode.postMessage({ type: 'readFile', filePath });
          // Timeout after 10 seconds
          setTimeout(() => {
            window.removeEventListener('message', handler);
            reject(new Error('File read timeout'));
          }, 10000);
        });
      case 'web':
      default:
        // For web, this would require user file selection
        throw new Error('File operations not supported in web environment. Use file input instead.');
    }
  },

  writeFile: async (filePath: string, content: string): Promise<void> => {
    const platform = detectPlatform();
    switch (platform) {
      case 'electron':
        return (window as any).electronAPI.writeFile(filePath, content);
      case 'vscode':
        const vscode = (window as any).acquireVsCodeApi();
        return new Promise((resolve, reject) => {
          const handler = (event: MessageEvent) => {
            if (event.data.type === 'fileWrite' && event.data.filePath === filePath) {
              window.removeEventListener('message', handler);
              if (event.data.success) {
                resolve();
              } else {
                reject(new Error(event.data.error));
              }
            }
          };
          window.addEventListener('message', handler);
          vscode.postMessage({ type: 'writeFile', filePath, content });
          setTimeout(() => {
            window.removeEventListener('message', handler);
            reject(new Error('File write timeout'));
          }, 10000);
        });
      case 'web':
      default:
        // For web, trigger download
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filePath.split('/').pop() || 'file.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return Promise.resolve();
    }
  },

  showOpenDialog: async (): Promise<string[]> => {
    const platform = detectPlatform();
    switch (platform) {
      case 'electron':
        const result = await (window as any).electronAPI.showOpenDialog();
        return result.filePaths || [];
      case 'vscode':
        const vscode = (window as any).acquireVsCodeApi();
        return new Promise((resolve) => {
          const handler = (event: MessageEvent) => {
            if (event.data.type === 'openDialogResult') {
              window.removeEventListener('message', handler);
              resolve(event.data.filePaths || []);
            }
          };
          window.addEventListener('message', handler);
          vscode.postMessage({ type: 'showOpenDialog' });
        });
      case 'web':
      default:
        // For web, use file input
        return new Promise((resolve) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.onchange = (e) => {
            const files = (e.target as HTMLInputElement).files;
            const paths = files ? Array.from(files).map(f => f.name) : [];
            resolve(paths);
          };
          input.click();
        });
    }
  },

  showSaveDialog: async (): Promise<string | null> => {
    const platform = detectPlatform();
    switch (platform) {
      case 'electron':
        const result = await (window as any).electronAPI.showSaveDialog();
        return result.filePath || null;
      case 'vscode':
        const vscode = (window as any).acquireVsCodeApi();
        return new Promise((resolve) => {
          const handler = (event: MessageEvent) => {
            if (event.data.type === 'saveDialogResult') {
              window.removeEventListener('message', handler);
              resolve(event.data.filePath || null);
            }
          };
          window.addEventListener('message', handler);
          vscode.postMessage({ type: 'showSaveDialog' });
        });
      case 'web':
      default:
        // For web, prompt for filename
        const fileName = prompt('Enter file name:');
        return fileName;
    }
  }
};

// Unified authentication handling
export const unifiedAuth = {
  getToken: async (): Promise<string | null> => {
    const platform = detectPlatform();
    switch (platform) {
      case 'vscode':
        // VSCode uses secrets API
        const vscode = (window as any).acquireVsCodeApi();
        return new Promise((resolve) => {
          const handler = (event: MessageEvent) => {
            if (event.data.type === 'getTokenResult') {
              window.removeEventListener('message', handler);
              resolve(event.data.token || null);
            }
          };
          window.addEventListener('message', handler);
          vscode.postMessage({ type: 'getToken' });
        });
      default:
        // Electron and web use IndexedDB
        try {
          const authData = await indexedDBService.getItem('auth_token');
          return authData?.token || null;
        } catch {
          return null;
        }
    }
  },

  setToken: async (token: string): Promise<void> => {
    const platform = detectPlatform();
    switch (platform) {
      case 'vscode':
        const vscode = (window as any).acquireVsCodeApi();
        vscode.postMessage({ type: 'setToken', token });
        break;
      default:
        await indexedDBService.setItem('auth_token', { token, timestamp: Date.now() });
    }
  },

  clearToken: async (): Promise<void> => {
    const platform = detectPlatform();
    switch (platform) {
      case 'vscode':
        const vscode = (window as any).acquireVsCodeApi();
        vscode.postMessage({ type: 'clearToken' });
        break;
      default:
        await indexedDBService.removeItem('auth_token');
    }
  }
};

// Create axios instance only if API_BASE_URL is set
const api = axios.create({
  baseURL: API_BASE_URL || 'http://api.bot.mawi.uk',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const authData = await indexedDBService.getItem('auth_token');
    if (authData?.token) {
      config.headers.Authorization = `Bearer ${authData.token}`;
    }
  } catch (error) {
    console.error('Failed to get auth token from IndexedDB:', error);
  }
  return config;
});

// Token refresh queue
let isRefreshing = false;
let failedRequestsQueue: any[] = [];

// Process failed requests after token refresh
const processFailedQueue = (token: string | null) => {
  failedRequestsQueue.forEach(({ resolve, reject, config }) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      resolve(api(config));
    } else {
      reject(new Error('Token refresh failed'));
    }
  });
  failedRequestsQueue = [];
};

// Handle auth errors with automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt token refresh
        const authData = await indexedDBService.getItem('auth_token');
        if (authData?.refresh_token) {
          const refreshResponse = await api.post('/api/auth/refresh', {
            refresh_token: authData.refresh_token
          });
          
          const newToken = refreshResponse.data.access_token;
          await indexedDBService.setItem('auth_token', {
            ...authData,
            token: newToken,
            expires_at: Date.now() + (refreshResponse.data.expires_in * 1000)
          });
          
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          processFailedQueue(newToken);
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        processFailedQueue(null);
      } finally {
        isRefreshing = false;
      }

      // If refresh fails, clear token and redirect
      try {
        await indexedDBService.removeItem('auth_token');
      } catch (storageError) {
        console.error('Failed to remove auth token from IndexedDB:', storageError);
      }
      
      window.location.href = '/auth';
    }
    
    return Promise.reject(error);
  }
);

// Mock delay helper
const mockDelay = async () => {
  if (USE_MOCK_API) {
    await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY));
  }
};

// Enhanced error handling for different platform constraints
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const platform = detectPlatform();

    // Handle network errors based on platform
    if (error.code === 'NETWORK_ERROR' || !error.response) {
      switch (platform) {
        case 'vscode':
          // VSCode might have proxy settings
          console.warn('Network error in VSCode environment. Check proxy settings.');
          break;
        case 'electron':
          // Electron might need different handling
          console.warn('Network error in Electron environment. Check connectivity.');
          break;
        case 'web':
          // Web environment - check CORS, etc.
          console.warn('Network error in web environment. Check CORS and connectivity.');
          break;
      }
    }

    // Handle auth errors
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      await unifiedAuth.clearToken();
      // Trigger login flow based on platform
      if (platform === 'vscode') {
        const vscode = (window as any).acquireVsCodeApi();
        vscode.postMessage({ type: 'showLogin' });
      } else {
        // For electron/web, redirect to login page
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Config API
// ============================================================================

export const getConfig = async (): Promise<Config> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return {
      status: true,
      name: 'Mawi Coder',
      version: '0.1.0',
      default_locale: 'en',
      cdn_base_url: import.meta.env.CDN_BASE_URL || 'https://cdn.mawi.local',
      features: {
        auth: false,
        auth_trusted_header: false,
        enable_api_keys: true,
        enable_signup: true,
        enable_login_form: true,
        enable_web_search: true,
        enable_google_drive_integration: false,
        enable_onedrive_integration: false,
        enable_image_generation: true,
        enable_admin_export: true,
        enable_admin_chat_access: true,
        enable_admin_analytics: true,
        enable_community_sharing: true,
        enable_memories: true,
        enable_autocomplete_generation: true,
        enable_direct_connections: true,
        enable_version_update_check: true,
      },
      default_prompt_suggestions: [
        { content: 'Explain quantum computing in simple terms', title: ['Explain', 'a complex topic'] },
        { content: 'Write a Python function to sort a list', title: ['Write', 'code'] },
      ],
    };
  }
  const response = await api.get('/api/config');
  return response.data;
};

export const getBanners = async (): Promise<Banner[]> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return [];
  }
  const response = await api.get('/api/config/banners');
  return response.data;
};

// ============================================================================
// Auth API
// ============================================================================

export const login = async (email: string, password: string): Promise<{ token: string; user: User }> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return {
      token: 'mock-token-' + Date.now(),
      user: {
        id: 'user-1',
        email,
        name: 'Demo User',
        role: 'admin',
        profile_image_url: '/user.png',
        permissions: {
          chat: { temporary: true, multiple_models: true, web_upload: true },
          features: { web_search: true, image_generation: true, code_interpreter: true },
        },
      },
    };
  }
  // Use FormData for OAuth2 password flow
  const formData = new FormData();
  formData.append('username', email);
  formData.append('password', password);
  const response = await api.post('/api/auth/login', formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return {
    token: response.data.access_token,
    user: response.data.user,
  };
};

export const signup = async (
  name: string,
  email: string,
  password: string
): Promise<{ token: string; user: User }> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return {
      token: 'mock-token-' + Date.now(),
      user: {
        id: 'user-' + Date.now(),
        email,
        name,
        role: 'pending',
        profile_image_url: '/user.png',
      },
    };
  }
  const response = await api.post('/api/auth/register', { email, password, full_name: name });
  // After registration, login to get token
  const loginResult = await login(email, password);
  return loginResult;
};

export const getCurrentUser = async (): Promise<User> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return {
      id: 'user-1',
      email: 'demo@mawichat.com',
      name: 'Demo User',
      role: 'admin',
      profile_image_url: '/user.png',
      permissions: {
        chat: { temporary: true, multiple_models: true, web_upload: true },
        features: { web_search: true, image_generation: true, code_interpreter: true },
      },
    };
  }
  const response = await api.get('/api/users/me');
  return response.data;
};

// ============================================================================
// Models API
// ============================================================================

export const getModels = async (refresh = false): Promise<Model[]> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return [
      {
        id: 'mawi-ai/tinyllama',
        name: 'TinyLlama',
        owned_by: 'ollama',
        info: {
          meta: {
            capabilities: { vision: false, web_search: false, usage: true },
            description: 'Lightweight local AI model',
          },
        },
      },
      {
        id: 'mawi-ai/phi4-mini:3.8b',
        name: 'Phi-4 Mini 3.8B',
        owned_by: 'ollama',
        info: {
          meta: {
            capabilities: { vision: false, web_search: false, usage: true },
            description: 'Microsoft Phi-4 Mini model',
          },
        },
      },
      {
        id: 'mawi-ai/llama3.2',
        name: 'Llama 3.2',
        owned_by: 'ollama',
        info: {
          meta: {
            capabilities: { vision: false, web_search: false, usage: true },
            description: 'Meta Llama 3.2 model',
          },
        },
      },
      {
        id: 'mawi-ai/qwen2.5',
        name: 'Qwen 2.5',
        owned_by: 'ollama',
        info: {
          meta: {
            capabilities: { vision: false, web_search: false, usage: true },
            description: 'Alibaba Qwen 2.5 model',
          },
        },
      },
      {
        id: 'mawi-ai/mistral',
        name: 'Mistral 7B',
        owned_by: 'ollama',
        info: {
          meta: {
            capabilities: { vision: false, web_search: false, usage: true },
            description: 'Mistral AI 7B model',
          },
        },
      },
      {
        id: 'gpt-4',
        name: 'GPT-4',
        owned_by: 'openai',
        info: {
          meta: {
            capabilities: { vision: true, web_search: true, usage: true },
            description: 'Most capable GPT-4 model',
          },
        },
      },
      {
        id: 'claude-3-opus',
        name: 'Claude 3 Opus',
        owned_by: 'openai',
        info: {
          meta: {
            capabilities: { vision: true, web_search: false, usage: true },
            description: 'Most capable Claude model',
          },
        },
      },
    ];
  }
  const response = await api.get('/api/models', { params: { refresh } });
  // Backend returns models in expected format
  return response.data || [];
};

// ============================================================================
// Chats API
// ============================================================================

export const getChatList = async (page = 1, limit = 20): Promise<Chat[]> => {
  if (USE_MOCK_API) {
    await mockDelay();
    const stored = await indexedDBService.getItem('chats');
    if (stored) {
      return JSON.parse(stored);
    }
    // Return sample chats
    return [
      {
        id: 'chat-1',
        title: 'Welcome to Mawi AI Assist',
        models: ['mawi-ai/llama3.2'],
        messages: [],
        history: { messages: {}, currentId: null },
        timestamp: Date.now() - 60000,
      },
    ];
  }
  const response = await api.get('/api/chats', { params: { page, limit } });
  // Transform backend response to frontend Chat format
  return response.data.map((chat: any) => ({
    id: chat.id,
    title: chat.title || 'New Chat',
    models: chat.models || [],
    messages: [],
    history: { messages: {}, currentId: null },
    timestamp: chat.timestamp || new Date(chat.updatedAt).getTime(),
  }));
};

export const getChatById = async (id: string): Promise<Chat> => {
  if (USE_MOCK_API) {
    await mockDelay();
    const stored = await indexedDBService.getItem('chats');
    if (stored) {
      const chats = JSON.parse(stored);
      return chats.find((c: Chat) => c.id === id);
    }
    throw new Error('Chat not found');
  }
  const response = await api.get(`/api/chats/${id}`);
  const chat = response.data;
  // Transform backend response to frontend Chat format
  return {
    id: chat.id,
    title: chat.title || 'New Chat',
    models: chat.models || [],
    messages: (chat.messages || []).map((msg: any) => ({
      id: msg.id,
      parentId: msg.parentId || null,
      childrenIds: msg.childrenIds || [],
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp || new Date(msg.createdAt).getTime(),
      model: msg.model || undefined,
      modelName: msg.modelName || msg.model || undefined,
      done: msg.done !== false, // Default to true if not specified
    })),
    history: chat.history || {
      messages: {},
      currentId: null,
    },
    timestamp: chat.timestamp || new Date(chat.createdAt).getTime(),
  };
};

export const createChat = async (chat: Partial<Chat>): Promise<Chat> => {
  if (USE_MOCK_API) {
    await mockDelay();
    const newChat: Chat = {
      id: 'chat-' + Date.now(),
      title: chat.title || 'New Chat',
      models: chat.models || ['mawi-ai/llama3.2'],
      messages: chat.messages || [],
      history: chat.history || { messages: {}, currentId: null },
      timestamp: Date.now(),
    };

    // Store in localStorage
    const stored = await indexedDBService.getItem('chats');
    const chats = stored ? JSON.parse(stored) : [];
    chats.unshift(newChat);
    await indexedDBService.setItem('chats', chats, 'chat');

    return newChat;
  }
  const response = await api.post('/api/chats', { title: chat.title || 'New Chat' });
  const data = response.data;
  // Transform backend response to frontend Chat format
  return {
    id: data.id,
    title: data.title || 'New Chat',
    models: data.models || [],
    messages: data.messages || [],
    history: data.history || { messages: {}, currentId: null },
    timestamp: data.timestamp || new Date(data.createdAt).getTime(),
  };
};

export const updateChat = async (id: string, updates: Partial<Chat>): Promise<Chat> => {
  if (USE_MOCK_API) {
    await mockDelay();
    const stored = await indexedDBService.getItem('chats');
    if (stored) {
      const chats = JSON.parse(stored);
      const index = chats.findIndex((c: Chat) => c.id === id);
      if (index !== -1) {
        chats[index] = { ...chats[index], ...updates };
        await indexedDBService.setItem('chats', chats, 'chat');
        return chats[index];
      }
    }
    throw new Error('Chat not found');
  }
  const response = await api.put(`/api/chats/${id}`, updates);
  return response.data;
};

export const deleteChat = async (id: string): Promise<void> => {
  if (USE_MOCK_API) {
    await mockDelay();
    const stored = await indexedDBService.getItem('chats');
    if (stored) {
      const chats = JSON.parse(stored);
      const filtered = chats.filter((c: Chat) => c.id !== id);
      localStorage.setItem('mawi-chat-chats', JSON.stringify(filtered));
    }
    return;
  }
  await api.delete(`/api/chats/${id}`);
};

export const archiveChat = async (id: string): Promise<void> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return;
  }
  await api.post(`/api/chats/${id}/archive`);
};

// ============================================================================
// Tools API
// ============================================================================

export const getTools = async (): Promise<Tool[]> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return [
      { id: 'web_search', name: 'Web Search', description: 'Search the web for information' },
      { id: 'image_gen', name: 'Image Generation', description: 'Generate images from text' },
      { id: 'code_interpreter', name: 'Code Interpreter', description: 'Execute code snippets' },
    ];
  }
  const response = await api.get('/api/tools');
  return response.data;
};

// ============================================================================
// Folders API
// ============================================================================

export const getFolders = async (): Promise<Folder[]> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return [
      { id: 'folder-1', name: 'Work', timestamp: Date.now(), chatIds: [] },
      { id: 'folder-2', name: 'Personal', timestamp: Date.now(), chatIds: [] },
    ];
  }
  const response = await api.get('/api/folders');
  return response.data;
};

export const createFolder = async (name: string): Promise<Folder> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return { id: 'folder-' + Date.now(), name, timestamp: Date.now(), chatIds: [] };
  }
  const response = await api.post('/api/folders', { name });
  return response.data;
};

// ============================================================================
// Tags API
// ============================================================================

export const getTags = async (): Promise<Tag[]> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return [
      { id: 'tag-1', name: 'Important', color: '#ef4444' },
      { id: 'tag-2', name: 'Work', color: '#3b82f6' },
    ];
  }
  const response = await api.get('/api/tags');
  return response.data;
};

// ============================================================================
// Knowledge API
// ============================================================================

export const getKnowledge = async (): Promise<Knowledge[]> => {
  if (USE_MOCK_API) {
    await mockDelay();
    return [];
  }
  const response = await api.get('/api/knowledge');
  return response.data;
};

// ============================================================================
// User Settings API
// ============================================================================

export const getUserSettings = async (): Promise<any> => {
  if (USE_MOCK_API) {
    await mockDelay();
    const stored = await indexedDBService.getItem('settings');
    return stored ? JSON.parse(stored) : {};
  }
  const response = await api.get('/api/users/settings');
  return response.data;
};

export const updateUserSettings = async (settings: any): Promise<void> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return;
  }

  await api.patch('/settings', settings);
};

// ===== PHASE 7: ADVANCED AI CODE GENERATION =====

export interface CodeGenerationRequest {
  prompt: string;
  language: string;
  context?: string;
  projectType?: string;
  framework?: string;
  complexity?: 'simple' | 'medium' | 'complex';
  includeTests?: boolean;
  includeDocumentation?: boolean;
}

export interface CodeCompletionRequest {
  code: string;
  cursorPosition: number;
  language: string;
  context?: string;
  projectFiles?: string[];
  maxSuggestions?: number;
}

export interface CodeAnalysisRequest {
  code: string;
  language: string;
  analysisType: 'quality' | 'security' | 'performance' | 'maintainability';
}

export interface CodeRefactoringRequest {
  code: string;
  language: string;
  refactoringType: 'extract_method' | 'rename' | 'simplify' | 'optimize' | 'add_patterns';
  context?: string;
}

export interface AutonomousAppGenerationRequest {
  description: string;
  technologies: string[];
  architecture: 'monolithic' | 'microservices' | 'serverless';
  features: string[];
  targetPlatform: 'web' | 'mobile' | 'desktop' | 'api';
  includeTests: boolean;
  includeDocumentation: boolean;
  deploymentTarget?: 'docker' | 'kubernetes' | 'aws' | 'azure' | 'gcp';
}

// Advanced Code Generation
export const generateCode = async (request: CodeGenerationRequest): Promise<{ code: string; explanation: string; confidence: number }> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      code: `// Generated ${request.language} code for: ${request.prompt}\n\nfunction example() {\n    console.log("Hello World");\n}`,
      explanation: "Generated basic function structure",
      confidence: 0.85
    };
  }

  const response = await api.post('/api/autonomous-agents/decide', {
    taskId: `code-gen-${Date.now()}`,
    taskDescription: request.prompt,
    taskParameters: {
      language: request.language,
      context: request.context,
      projectType: request.projectType,
      framework: request.framework,
      complexity: request.complexity,
      includeTests: request.includeTests,
      includeDocumentation: request.includeDocumentation
    }
  });

  return response.data;
};

// Context-Aware Code Completion
export const getCodeCompletion = async (request: CodeCompletionRequest): Promise<{
  suggestions: Array<{
    code: string;
    explanation: string;
    confidence: number;
    type: 'completion' | 'refactoring' | 'optimization'
  }>
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      suggestions: [
        {
          code: 'console.log("Hello World");',
          explanation: "Complete the console.log statement",
          confidence: 0.9,
          type: 'completion'
        }
      ]
    };
  }

  const response = await api.post('/api/autonomous-agents/decide', {
    taskId: `completion-${Date.now()}`,
    taskDescription: `Complete this ${request.language} code with context-aware suggestions`,
    taskParameters: {
      code: request.code,
      cursorPosition: request.cursorPosition,
      language: request.language,
      context: request.context,
      projectFiles: request.projectFiles,
      maxSuggestions: request.maxSuggestions || 5
    }
  });

  return response.data;
};

// Advanced Code Analysis
export const analyzeCode = async (request: CodeAnalysisRequest): Promise<{
  score: number;
  issues: Array<{
    type: 'error' | 'warning' | 'info';
    message: string;
    line: number;
    severity: 'low' | 'medium' | 'high';
    suggestion?: string;
  }>;
  recommendations: string[];
  metrics: {
    complexity: number;
    maintainability: number;
    testability: number;
    security: number;
  };
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      score: 85,
      issues: [
        {
          type: 'warning',
          message: 'Consider adding error handling',
          line: 5,
          severity: 'medium',
          suggestion: 'Wrap in try-catch block'
        }
      ],
      recommendations: ['Add input validation', 'Improve error handling'],
      metrics: {
        complexity: 3.2,
        maintainability: 78,
        testability: 82,
        security: 85
      }
    };
  }

  const response = await api.post('/api/code-analysis/analyze', request);
  return response.data;
};

// Intelligent Code Refactoring
export const refactorCode = async (request: CodeRefactoringRequest): Promise<{
  refactoredCode: string;
  explanation: string;
  benefits: string[];
  confidence: number;
  alternativeSuggestions?: Array<{
    code: string;
    explanation: string;
    benefits: string[];
  }>;
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      refactoredCode: request.code.replace(/function/g, 'const'),
      explanation: "Converted to arrow function syntax",
      benefits: ['Improved readability', 'Modern syntax'],
      confidence: 0.88
    };
  }

  const response = await api.post('/api/autonomous-agents/decide', {
    taskId: `refactor-${Date.now()}`,
    taskDescription: `Refactor this ${request.language} code using ${request.refactoringType} pattern`,
    taskParameters: request
  });

  return response.data;
};

// Autonomous Application Generation
export const generateApplication = async (request: AutonomousAppGenerationRequest): Promise<{
  projectStructure: {
    [path: string]: {
      type: 'file' | 'directory';
      content?: string;
      language?: string;
    };
  };
  deploymentConfig: {
    dockerfile?: string;
    dockerCompose?: string;
    deploymentScripts?: { [name: string]: string };
  };
  documentation: {
    readme: string;
    apiDocs?: string;
    setupGuide: string;
  };
  nextSteps: string[];
  estimatedCompletionTime: string;
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      projectStructure: {
        'src/main.js': {
          type: 'file',
          content: 'console.log("Hello World");',
          language: 'javascript'
        },
        'package.json': {
          type: 'file',
          content: '{"name": "generated-app"}',
          language: 'json'
        }
      },
      deploymentConfig: {
        dockerfile: 'FROM node:18\nCOPY . .\nRUN npm install\nCMD ["npm", "start"]'
      },
      documentation: {
        readme: '# Generated Application',
        setupGuide: 'Run npm install && npm start'
      },
      nextSteps: ['Install dependencies', 'Run the application', 'Customize as needed'],
      estimatedCompletionTime: '2 minutes'
    };
  }

  const response = await api.post('/api/autonomous-agents/decide', {
    taskId: `app-gen-${Date.now()}`,
    taskDescription: `Generate a complete ${request.targetPlatform} application with the following requirements: ${request.description}`,
    taskParameters: request
  });

  return response.data;
};

// Advanced NLP Analysis (from Phase 6)
export const analyzeTextNLP = async (text: string, domain = 'code'): Promise<{
  intent: { primaryIntent: string; confidence: number };
  entities: Array<{ text: string; type: string; confidence: number }>;
  sentiment: { sentiment: string; score: number; confidence: number };
  summary: string;
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      intent: { primaryIntent: 'code_generation', confidence: 0.9 },
      entities: [{ text: 'function', type: 'keyword', confidence: 0.95 }],
      sentiment: { sentiment: 'neutral', score: 0.5, confidence: 0.8 },
      summary: 'Request for code generation'
    };
  }

  const response = await api.post('/api/nlp/analyze', {
    text,
    domain,
    language: 'en'
  });

  return {
    intent: response.data.intent,
    entities: response.data.entities,
    sentiment: response.data.sentiment,
    summary: `Analysis complete with ${response.data.confidence}% confidence`
  };
};

// Code Pattern Recognition
export const recognizePatterns = async (code: string, language: string): Promise<{
  patterns: Array<{
    type: string;
    confidence: number;
    location: { start: number; end: number };
    suggestion?: string;
    refactoring?: string;
  }>;
  antiPatterns: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high';
    location: { start: number; end: number };
    recommendation: string;
  }>;
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      patterns: [
        {
          type: 'factory_pattern',
          confidence: 0.8,
          location: { start: 10, end: 25 },
          suggestion: 'Consider using factory pattern here'
        }
      ],
      antiPatterns: [
        {
          type: 'long_method',
          severity: 'medium',
          location: { start: 1, end: 50 },
          recommendation: 'Break down into smaller methods'
        }
      ]
    };
  }

  const response = await api.post('/api/autonomous-agents/decide', {
    taskId: `pattern-${Date.now()}`,
    taskDescription: `Analyze ${language} code for patterns and anti-patterns`,
    taskParameters: { code, language }
  });

  return response.data;
};

// Intelligent Documentation Generation
export const generateDocumentation = async (
  code: string,
  language: string,
  docType: 'comments' | 'readme' | 'api' | 'inline'
): Promise<{
  documentation: string;
  coverage: number; // percentage of code documented
  suggestions: string[];
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      documentation: `/**\n * Generated documentation for ${language} code\n */`,
      coverage: 75,
      suggestions: ['Add parameter descriptions', 'Include return value documentation']
    };
  }

  const response = await api.post('/api/autonomous-agents/decide', {
    taskId: `docs-${Date.now()}`,
    taskDescription: `Generate ${docType} documentation for this ${language} code`,
    taskParameters: { code, language, docType }
  });

  return response.data;
};

// ===== PHASE 7.4: INTELLIGENCE LAYER - ADVANCED ANALYSIS =====

export interface SecurityScanRequest {
  code: string;
  language: string;
  scanType: 'basic' | 'comprehensive' | 'custom';
  customRules?: string[];
}

export interface PerformanceAnalysisRequest {
  code: string;
  language: string;
  context?: string;
  targetEnvironment?: 'browser' | 'server' | 'mobile';
}

export interface CodeReviewRequest {
  code: string;
  language: string;
  reviewType: 'basic' | 'comprehensive' | 'security' | 'performance';
  reviewerLevel?: 'junior' | 'senior' | 'expert';
}

export interface MaintainabilityAnalysisRequest {
  code: string;
  language: string;
  metrics: ('complexity' | 'readability' | 'testability' | 'modularity' | 'documentation')[];
}

// Security Vulnerability Scanner
export const scanSecurityVulnerabilities = async (request: SecurityScanRequest): Promise<{
  vulnerabilities: Array<{
    type: 'injection' | 'xss' | 'csrf' | 'auth_bypass' | 'data_exposure' | 'weak_crypto';
    severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
    location: { start: number; end: number; file?: string };
    description: string;
    impact: string;
    recommendation: string;
    cwe?: string; // Common Weakness Enumeration
    confidence: number;
  }>;
  summary: {
    totalVulnerabilities: number;
    criticalCount: number;
    highCount: number;
    overallRisk: 'low' | 'medium' | 'high' | 'critical';
  };
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      vulnerabilities: [
        {
          type: 'injection',
          severity: 'high',
          location: { start: 15, end: 15 },
          description: 'Potential SQL injection vulnerability',
          impact: 'Database compromise possible',
          recommendation: 'Use parameterized queries',
          cwe: 'CWE-89',
          confidence: 0.85
        }
      ],
      summary: {
        totalVulnerabilities: 1,
        criticalCount: 0,
        highCount: 1,
        overallRisk: 'medium'
      }
    };
  }

  const response = await api.post('/api/code-analysis/security-scan', request);
  return response.data;
};

// Performance Optimization AI
export const analyzePerformance = async (request: PerformanceAnalysisRequest): Promise<{
  bottlenecks: Array<{
    type: 'cpu' | 'memory' | 'io' | 'network' | 'rendering';
    location: { start: number; end: number };
    description: string;
    impact: 'low' | 'medium' | 'high';
    optimization: string;
    estimatedImprovement: string;
    confidence: number;
  }>;
  metrics: {
    timeComplexity: string;
    spaceComplexity: string;
    performanceScore: number;
    optimizationOpportunities: number;
  };
  recommendations: Array<{
    priority: 'high' | 'medium' | 'low';
    description: string;
    implementation: string;
    expectedBenefit: string;
  }>;
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      bottlenecks: [
        {
          type: 'cpu',
          location: { start: 20, end: 30 },
          description: 'Inefficient loop with O(n²) complexity',
          impact: 'high',
          optimization: 'Use hash map for O(1) lookups',
          estimatedImprovement: '90% faster execution',
          confidence: 0.88
        }
      ],
      metrics: {
        timeComplexity: 'O(n²)',
        spaceComplexity: 'O(n)',
        performanceScore: 65,
        optimizationOpportunities: 3
      },
      recommendations: [
        {
          priority: 'high',
          description: 'Optimize nested loops',
          implementation: 'Replace with hash-based lookup',
          expectedBenefit: 'Significant performance improvement'
        }
      ]
    };
  }

  const response = await api.post('/api/code-analysis/performance', request);
  return response.data;
};

// Automated Code Review System
export const performCodeReview = async (request: CodeReviewRequest): Promise<{
  overallScore: number;
  reviewComments: Array<{
    type: 'style' | 'logic' | 'security' | 'performance' | 'maintainability' | 'documentation';
    severity: 'error' | 'warning' | 'info' | 'suggestion';
    line: number;
    message: string;
    suggestion: string;
    codeExample?: string;
    references?: string[];
  }>;
  summary: {
    strengths: string[];
    weaknesses: string[];
    recommendations: string[];
    nextSteps: string[];
  };
  metadata: {
    reviewer: string;
    reviewType: string;
    reviewDate: string;
    codeLines: number;
    functions: number;
    complexity: number;
  };
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      overallScore: 78,
      reviewComments: [
        {
          type: 'style',
          severity: 'warning',
          line: 12,
          message: 'Inconsistent naming convention',
          suggestion: 'Use camelCase for variable names',
          codeExample: 'const userName = "John";'
        },
        {
          type: 'logic',
          severity: 'info',
          line: 25,
          message: 'Consider early return pattern',
          suggestion: 'Use guard clauses to reduce nesting',
          codeExample: 'if (!condition) return;'
        }
      ],
      summary: {
        strengths: ['Good error handling', 'Clear function structure'],
        weaknesses: ['Could improve documentation', 'Some complex functions'],
        recommendations: ['Add JSDoc comments', 'Break down complex functions'],
        nextSteps: ['Review documentation', 'Refactor complex methods']
      },
      metadata: {
        reviewer: 'AI Code Reviewer',
        reviewType: 'comprehensive',
        reviewDate: new Date().toISOString(),
        codeLines: 150,
        functions: 8,
        complexity: 4.2
      }
    };
  }

  const response = await api.post('/api/code-analysis/review', request);
  return response.data;
};

// Maintainability Scoring System
export const analyzeMaintainability = async (request: MaintainabilityAnalysisRequest): Promise<{
  overallScore: number; // 0-100
  scores: {
    complexity: number;
    readability: number;
    testability: number;
    modularity: number;
    documentation: number;
  };
  issues: Array<{
    category: 'complexity' | 'readability' | 'testability' | 'modularity' | 'documentation';
    severity: 'high' | 'medium' | 'low';
    description: string;
    location: { start: number; end: number };
    suggestion: string;
    impact: string;
  }>;
  recommendations: Array<{
    priority: 'high' | 'medium' | 'low';
    category: string;
    description: string;
    effort: 'low' | 'medium' | 'high';
    benefit: 'low' | 'medium' | 'high';
  }>;
  metrics: {
    linesOfCode: number;
    cyclomaticComplexity: number;
    halsteadVolume: number;
    maintainabilityIndex: number;
    commentRatio: number;
    functionLength: { average: number; max: number };
    dependencyCount: number;
  };
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      overallScore: 72,
      scores: {
        complexity: 65,
        readability: 78,
        testability: 70,
        modularity: 80,
        documentation: 60
      },
      issues: [
        {
          category: 'documentation',
          severity: 'medium',
          description: 'Missing function documentation',
          location: { start: 10, end: 15 },
          suggestion: 'Add JSDoc comments to exported functions',
          impact: 'Reduced code maintainability'
        },
        {
          category: 'complexity',
          severity: 'high',
          description: 'High cyclomatic complexity in main function',
          location: { start: 20, end: 50 },
          suggestion: 'Break down into smaller, focused functions',
          impact: 'Harder to test and maintain'
        }
      ],
      recommendations: [
        {
          priority: 'high',
          category: 'documentation',
          description: 'Add comprehensive JSDoc comments',
          effort: 'medium',
          benefit: 'high'
        },
        {
          priority: 'medium',
          category: 'modularity',
          description: 'Extract utility functions',
          effort: 'high',
          benefit: 'high'
        }
      ],
      metrics: {
        linesOfCode: 245,
        cyclomaticComplexity: 8.5,
        halsteadVolume: 1250,
        maintainabilityIndex: 68,
        commentRatio: 15,
        functionLength: { average: 25, max: 45 },
        dependencyCount: 3
      }
    };
  }

  const response = await api.post('/api/code-analysis/maintainability', request);
  return response.data;
};

// Advanced AST-based Code Analysis
export const analyzeCodeAST = async (code: string, language: string): Promise<{
  ast: any; // Abstract Syntax Tree
  semanticAnalysis: {
    variables: Array<{
      name: string;
      type: string;
      scope: 'global' | 'local' | 'parameter';
      usage: 'read' | 'write' | 'readwrite';
      locations: number[];
    }>;
    functions: Array<{
      name: string;
      parameters: Array<{ name: string; type: string }>;
      returnType: string;
      complexity: number;
      dependencies: string[];
      location: { start: number; end: number };
    }>;
    classes: Array<{
      name: string;
      methods: string[];
      properties: string[];
      inheritance: string[];
      location: { start: number; end: number };
    }>;
  };
  issues: Array<{
    type: 'syntax' | 'semantic' | 'logic' | 'style';
    severity: 'error' | 'warning' | 'info';
    message: string;
    location: { start: number; end: number };
    suggestion?: string;
  }>;
  insights: {
    patterns: string[];
    antiPatterns: string[];
    improvements: string[];
  };
}> => {
  if (USE_MOCK_API) {
    await new Promise(resolve => setTimeout(resolve, MOCK_DELAY));
    return {
      ast: { type: 'Program', body: [] }, // Simplified AST
      semanticAnalysis: {
        variables: [
          {
            name: 'user',
            type: 'object',
            scope: 'local',
            usage: 'readwrite',
            locations: [15, 22, 28]
          }
        ],
        functions: [
          {
            name: 'processData',
            parameters: [{ name: 'data', type: 'array' }],
            returnType: 'object',
            complexity: 3,
            dependencies: ['lodash', 'axios'],
            location: { start: 10, end: 30 }
          }
        ],
        classes: []
      },
      issues: [
        {
          type: 'style',
          severity: 'warning',
          message: 'Unused variable detected',
          location: { start: 5, end: 5 },
          suggestion: 'Remove or use the variable'
        }
      ],
      insights: {
        patterns: ['Factory pattern detected'],
        antiPatterns: ['Deep nesting detected'],
        improvements: ['Consider using early returns']
      }
    };
  }

  const response = await api.post('/api/code-analysis/ast', { code, language });
  return response.data;
};

export default api;